<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AppCard from "@/components/AppCard";
import AppPlan from "@/components/AppPlan";
import { store } from "@/store";
import { capitalize } from "@/utils/formatters";
import useApi from "@/composables/useApi";

const { service } = useApi();

const user = ref({});
const services = ref([]);
const loading = ref(false);

const firstName = computed(() => {
  if (store.user?.name) {
    return capitalize(store.user.name).split(" ")[0];
  }

  return "";
});

const getServices = async (user_id) => {
  try {
    console.log("getServices");
    const data = await service.listServicesByUser(user_id);
    if (data && data.payload) {
      services.value = data.payload;
      store.services = data.payload;
    }
  } catch (error) {
    console.error("Error getting services:", error);
  }
};

onMounted(async () => {
  try {
    // store.APP_BACK_BUTTON = true;
    store.APP_LOADING = true;
    // The greeting below acts as the page heading, so drop the generic
    // "Inicio" title the router sets on every route
    store.APP_TITLE = "";
    user.value = store.user;

    // Check if we already have services in store
    if (store.services && store.services.length > 0) {
      services.value = store.services;
    } else if (user.value && user.value.id) {
      // If not, fetch them if we have a user
      await getServices(user.value.id);
    }
  } catch (error) {
    console.error(error);
  } finally {
    store.APP_LOADING = false;
  }
});

// Watch for changes in store.user and store.services
watch(
  () => store.user,
  async (newUser) => {
    if (
      newUser &&
      newUser.id &&
      (!services.value.length || !store.services.length)
    ) {
      await getServices(newUser.id);
    }
  },
  { deep: true }
);

watch(
  () => store.services,
  (newServices) => {
    if (newServices && newServices.length && !services.value.length) {
      services.value = newServices;
    }
  },
  { deep: true }
);
</script>
<template lang="pug">
//- pre | {{ user }}
//- pre | {{ services }}
.home
  header.home__header
    h1.home__greeting(v-if="firstName") {{ $t("homepage.hello", { name: firstName }) }}
    p.home__subtitle {{ $t("homepage.subtitle") }}
  .home__section
    h2.home__title {{ $t("homepage.my_services") }}
    span.home__count(v-if="services.length") {{ services.length }}
  .home__body(v-if="services.length")
    app-plan(v-for="service in services" :key="service.id" :plan="service")
  AppCard.home__empty(v-else padding="lg")
    i.uil.uil-wifi-slash(aria-hidden="true")
    h3 {{ $t("homepage.empty_title") }}
    p {{ $t("homepage.empty_subtitle") }}
</template>

<style lang="scss" scoped>
.home {
  @apply pb-6;

  &__header {
    @apply mb-6;
  }

  &__greeting {
    @apply text-2xl;
    @apply font-semibold;
    @apply text-bluegray-700;
    @apply leading-tight;
  }

  &__subtitle {
    @apply text-sm;
    @apply text-bluegray-400;
    @apply mt-1;
  }

  &__section {
    @apply flex;
    @apply items-center;
    @apply gap-2;
    @apply mb-3;
  }

  &__title {
    @apply font-semibold;
    @apply text-bluegray-600;
    @apply text-base;
  }

  &__count {
    @apply inline-flex;
    @apply items-center;
    @apply justify-center;
    @apply h-5;
    min-width: 1.25rem;
    @apply px-1.5;
    @apply rounded-full;
    @apply bg-bluegray-200;
    @apply text-bluegray-600;
    @apply text-xs;
    @apply font-semibold;
    @apply leading-none;
  }

  &__body {
    @apply grid;
    @apply grid-cols-1;
    @apply gap-4;
    @apply items-stretch;
    @screen lg {
      @apply grid-cols-2;
    }
    @screen 2xl {
      @apply grid-cols-3;
    }
  }

  &__empty {
    @apply flex;
    @apply flex-col;
    @apply items-center;
    @apply text-center;

    i {
      @apply text-4xl;
      @apply text-bluegray-300;
      @apply mb-3;
    }

    h3 {
      @apply font-semibold;
      @apply text-bluegray-600;
    }

    p {
      @apply text-sm;
      @apply text-bluegray-400;
      @apply mt-1;
    }
  }
}
</style>
